import {
  BLCMasterData,
  BoatHullType,
  LookupTable,
  tableIdFor,
} from '../data/blcTables'

export interface BLCMeasurement {
  hullType: BoatHullType | ''
  le: string
  bf: string
  bm: string
  bb: string
  de: string
  dl: string
  purpose: 'actual' | 'capacity'
}

export interface LookupResult {
  value: number
  log: number | null
  inRange: boolean
  rangeLabel: string
}

export interface BLCResult {
  bav: number
  dis: number
  le: LookupResult
  bavLookup: LookupResult
  disLookup: LookupResult
  total: number | null
  loadKg: number | null
  loadMaund: number | null
  loadTons: number | null
  billableUnits: number | null
  annualFee: number | null
  totalInRange: boolean
  warnings: string[]
  complete: boolean
}

const num = (value: string): number => {
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) ? parsed : NaN
}

export function tableRange(table: LookupTable): string {
  if (table.rows.length === 0) return '—'
  const first = table.rows[0].base
  const last = table.rows[table.rows.length - 1].base
  const step = table.colStep
  const max = last + step * 9
  const decimals = step === 0.01 ? 2 : 1
  return `${first.toFixed(decimals)} – ${max.toFixed(decimals)}`
}

/** Looks up a value in a 2D log grid (row = integer part, column = decimal part). */
export function lookupInTable(
  table: LookupTable,
  rawValue: number,
): LookupResult {
  const rangeLabel = tableRange(table)
  if (!Number.isFinite(rawValue)) {
    return {
      value: rawValue,
      log: null,
      inRange: false,
      rangeLabel,
    }
  }
  const step = table.colStep
  // Snap the value onto the grid resolution before splitting it.
  const snapped = Math.round(rawValue / step) * step
  const base = Math.round(Math.floor(snapped / (step * 10)) * (step * 10) * 1000) / 1000
  const colIndex = Math.round((snapped - base) / step)
  const row = table.rows.find((r) => Math.abs(r.base - base) < step / 2)
  if (!row || colIndex < 0 || colIndex > 9 || row.values[colIndex] == null) {
    return {
      value: snapped,
      log: null,
      inRange: false,
      rangeLabel,
    }
  }
  return {
    value: snapped,
    log: row.values[colIndex],
    inRange: true,
    rangeLabel,
  }
}

export function calculateBLC(
  measurement: BLCMeasurement,
  master: BLCMasterData,
): BLCResult {
  const warnings: string[] = []
  const le = num(measurement.le)
  const bf = num(measurement.bf)
  const bm = num(measurement.bm)
  const bb = num(measurement.bb)
  const de = num(measurement.de)
  const dl = num(measurement.dl)

  const bav = Number(((bf + bm + bb) / 3).toFixed(2))
  const dis = Number((de - dl).toFixed(2))

  const complete =
    measurement.hullType !== '' &&
    [le, bf, bm, bb, de, dl].every((v) => Number.isFinite(v))

  const hullType = (measurement.hullType || 'Type C') as BoatHullType
  const leTable = master.tables[tableIdFor('Le', hullType)]
  const bavTable = master.tables[tableIdFor('Bav', hullType)]
  const disTable = master.tables[tableIdFor('Dis', hullType)]
  const antilogTable = master.tables.Antilog_Kg

  const leLookup = lookupInTable(leTable, le)
  const bavLookup = lookupInTable(bavTable, bav)
  const disLookup = lookupInTable(disTable, dis)

  if (complete && !leLookup.inRange) {
    warnings.push(
      `Le = ${le} m is outside the ${hullType} Le table (${leLookup.rangeLabel} m) — needs manual review.`,
    )
  }
  if (complete && !bavLookup.inRange) {
    warnings.push(
      `Bav = ${bav} m is outside the ${hullType} Bav table (${bavLookup.rangeLabel} m) — needs manual review.`,
    )
  }
  if (complete && !disLookup.inRange) {
    warnings.push(
      `Dis = ${dis} cm is outside the ${hullType} displacement table (${disLookup.rangeLabel} cm) — needs manual review.`,
    )
  }

  let total: number | null = null
  let loadKg: number | null = null
  let totalInRange = false

  if (
    complete &&
    leLookup.log != null &&
    bavLookup.log != null &&
    disLookup.log != null
  ) {
    total = Number((leLookup.log + bavLookup.log + disLookup.log).toFixed(2))
    const antilog = lookupInTable(antilogTable, total)
    totalInRange = antilog.inRange
    loadKg = antilog.log
    if (!antilog.inRange) {
      warnings.push(
        `Boat dimensions produce a value outside the standard BLC tables — refer to manual Example 3 (split-and-add) calculation.`,
      )
    }
  }

  const loadMaund =
    loadKg != null ? Math.round(loadKg / master.maundFactor) : null
  const loadTons = loadKg != null ? Number((loadKg / 1000).toFixed(2)) : null
  const billableUnits =
    loadKg != null ? Math.ceil(loadKg / master.feeConfig.unitKg) : null
  const annualFee =
    billableUnits != null ? billableUnits * master.feeConfig.ratePerUnit : null

  return {
    bav: Number.isFinite(bav) ? bav : 0,
    dis: Number.isFinite(dis) ? dis : 0,
    le: leLookup,
    bavLookup,
    disLookup,
    total,
    loadKg,
    loadMaund,
    loadTons,
    billableUnits,
    annualFee,
    totalInRange,
    warnings,
    complete,
  }
}

export function gridToCsv(table: LookupTable): string {
  const step = table.colStep
  const colDecimals = step === 0.01 ? 2 : 1
  const header = [
    table.rowLabel,
    ...Array.from({ length: 10 }, (_, i) => (i * step).toFixed(colDecimals)),
  ]
  const lines = [header.join(',')]
  table.rows.forEach((row) => {
    lines.push(
      [row.base.toFixed(step === 0.01 ? 2 : 0), ...row.values].join(','),
    )
  })
  return lines.join('\n')
}

export function csvToRows(csv: string): { base: number; values: number[] }[] {
  return csv
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .slice(1)
    .map((line) => {
      const cells = line.split(',').map((c) => c.trim())
      const base = parseFloat(cells[0])
      const values = Array.from({ length: 10 }, (_, i) => {
        const parsed = parseFloat(cells[i + 1])
        return Number.isFinite(parsed) ? parsed : 0
      })
      return {
        base: Number.isFinite(base) ? base : 0,
        values,
      }
    })
    .filter((row) => Number.isFinite(row.base))
}

export function downloadCsv(fileName: string, csv: string): void {
  const blob = new Blob([csv], {
    type: 'text/csv;charset=utf-8;',
  })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
